import isObject from 'lodash/isObject';
import size from 'lodash/size';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {USER_DATA} from 'user/user-prop-types';
import {connect} from 'react-redux';
import classNames from 'classnames';
import {compose} from 'redux';
import IconCheck from '@spothero/icons/check';
import Alert from '@spothero/ui/alert';
import Button from '@spothero/ui/button';
import TextButton from '@spothero/ui/text-button';
import withConfig from 'config/withConfig';
import UserUtils from 'user/user-utils';
import FormatUtils from 'utils/format';
import CheckoutUtils from '../checkout-utils';
import {updateData} from '../checkout-actions';
import CheckoutPanel from '../common/CheckoutPanel';
import BillingFrequency from './BillingFrequency';
import MonthlyReservation from './MonthlyReservation';
import TransientReservation from './TransientReservation';

class ReservationDetails extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        isFirstView: PropTypes.bool.isRequired,
        user: PropTypes.shape(USER_DATA).isRequired,
        isMonthly: PropTypes.bool,
        starts: PropTypes.string,
        selectedRate: PropTypes.object,
        hourlyRates: PropTypes.arrayOf(PropTypes.object),
        monthlyRates: PropTypes.arrayOf(PropTypes.object),
        reservationDetailsSkipped: PropTypes.bool.isRequired,
        reservationDetailsEdit: PropTypes.bool.isRequired,
        phoneNumberRequired: PropTypes.bool,
        vehicleSelectPresent: PropTypes.bool,
        supportsOversizeFeeType: PropTypes.bool,
        vehicleSelectRequired: PropTypes.bool,
        vehicleMakeModelPlate: PropTypes.string,
        vehicleColor: PropTypes.string,
        isVehicleUnknown: PropTypes.bool,
        licensePlateRequired: PropTypes.bool,
        contractFullName: PropTypes.string,
        purchaseOnBehalfOfCustomer: PropTypes.bool,
        purchaseForCustomer: PropTypes.bool,
        isRebookingReservation: PropTypes.bool,
        onContinue: PropTypes.func.isRequired,
        onUpdateData: PropTypes.func.isRequired,
        onVehicleChange: PropTypes.func.isRequired,
        isLoadingRates: PropTypes.bool.isRequired,
    };
    state = {
        iDontKnowVehicleSelected: false,
        termsError: false,
    };

    _onIDontKnowChanged = status => {
        this.setState({
            iDontKnowVehicleSelected: status,
        });
    };

    _onEditClick = () => {
        this.props.onUpdateData({
            reservationDetailsSkipped: false,
            reservationDetailsEdit: true,
        });
    };

    _onContinueClick = async evt => {
        const {
            isMonthly,
            vehicleSelectRequired,
            licensePlateRequired,
            phoneNumberRequired,
            onUpdateData,
            onContinue,
        } = this.props;
        const reservationDetailsData = await this.submit();
        const {iDontKnowVehicleSelected} = this.state;

        if (
            (vehicleSelectRequired || licensePlateRequired) &&
            (!iDontKnowVehicleSelected &&
                !reservationDetailsData.vehicleProfileId &&
                !reservationDetailsData.vehicleInfoId &&
                !reservationDetailsData.unlistedModel)
        ) {
            return false;
        }

        if (phoneNumberRequired && !reservationDetailsData.phone) {
            return false;
        }

        if (reservationDetailsData) {
            if (
                !isMonthly &&
                !vehicleSelectRequired &&
                !licensePlateRequired &&
                !phoneNumberRequired &&
                !size(reservationDetailsData)
            ) {
                reservationDetailsData.reservationDetailsSkipped = true;
            }

            reservationDetailsData.reservationDetailsEdit = false;

            onContinue();
            onUpdateData(reservationDetailsData);
        }
    };

    _renderPreview() {
        const {
            user: {phoneNumber},
            isMonthly,
            starts,
            selectedRate,
            phoneNumberRequired,
            vehicleSelectRequired,
            vehicleMakeModelPlate,
            vehicleColor,
            isVehicleUnknown,
            licensePlateRequired,
            contractFullName,
        } = this.props;

        if (!selectedRate) {
            return null;
        }

        const {iDontKnowVehicleSelected} = this.state;
        const userPhone = phoneNumber ? (
            <div>{FormatUtils.phone(phoneNumber)}</div>
        ) : null;
        const {title: rateTitle, recurrable} = selectedRate;
        const {startDateFormatted, endDateFormatted} = isMonthly
            ? CheckoutUtils.getMonthlyReservationInfo(selectedRate, starts)
            : {startDateFormatted: null, endDateFormatted: null};
        let plateAlert = null;

        if (
            (phoneNumberRequired && !userPhone) ||
            ((vehicleSelectRequired || isMonthly || licensePlateRequired) &&
                (!iDontKnowVehicleSelected &&
                    !isVehicleUnknown &&
                    !vehicleMakeModelPlate)) ||
            (selectedRate && isMonthly && (!vehicleColor || !contractFullName))
        ) {
            return null;
        }

        if (licensePlateRequired && vehicleMakeModelPlate) {
            if (vehicleMakeModelPlate.includes(`I don't know`)) {
                plateAlert = (
                    <Alert type="highlight">
                        Update to vehicle with a valid license plate number
                        before parking.
                    </Alert>
                );
            }
        }

        if (
            userPhone ||
            plateAlert ||
            vehicleMakeModelPlate ||
            licensePlateRequired ||
            iDontKnowVehicleSelected
        ) {
            const showVehiclePreview =
                plateAlert ||
                (vehicleMakeModelPlate &&
                    (vehicleSelectRequired ||
                        isMonthly ||
                        licensePlateRequired)) ||
                iDontKnowVehicleSelected;

            return (
                <Fragment>
                    {isMonthly && (
                        <Fragment>
                            <span className="CheckoutPanel-preview-rate">
                                <h3>Rate Details</h3>
                                {rateTitle}
                            </span>
                            <span className="CheckoutPanel-preview-reservation">
                                <h3>Reservation</h3>
                                {startDateFormatted} - {endDateFormatted}
                            </span>
                            <span className="CheckoutPanel-preview-billing-frequency">
                                <h3>Billing Frequency</h3>
                                <BillingFrequency recurrable={recurrable} />
                            </span>
                        </Fragment>
                    )}
                    {showVehiclePreview && (
                        <span className="CheckoutPanel-preview-vehicle">
                            <h3>Vehicle Information</h3>
                            {vehicleMakeModelPlate &&
                                (vehicleSelectRequired ||
                                    isMonthly ||
                                    licensePlateRequired) && (
                                    <div>{vehicleMakeModelPlate}</div>
                                )}
                            {plateAlert}
                            {iDontKnowVehicleSelected && vehicleSelectRequired && (
                                <Alert type="highlight">
                                    This spot may charge fees onsite for
                                    oversized vehicles.
                                    <br />
                                    Enter vehicle details before parking to see
                                    estimated fees.
                                </Alert>
                            )}
                        </span>
                    )}
                    {(userPhone || contractFullName) && (
                        <span className="CheckoutPanel-preview-phone lo_sensitive">
                            <h3>Contact Information</h3>
                            {contractFullName}
                            {userPhone}
                        </span>
                    )}
                </Fragment>
            );
        } else {
            return null;
        }
    }

    _renderReservationDetailEdit() {
        return (
            <TextButton
                className="CheckoutPanel-title-actions"
                onClick={this._onEditClick}
            >
                Edit
            </TextButton>
        );
    }

    async submit() {
        const {
            licensePlateRequired,
            vehicleSelectPresent,
            vehicleSelectRequired,
            supportsOversizeFeeType,
            phoneNumberRequired,
            isMonthly,
            selectedRate: {terms_and_conditions: termsURL},
        } = this.props;

        const vehicleColorSelectForm = isMonthly
            ? this._monthlyReservation.vehicleColorSelectForm
            : null;
        const phoneForm = isMonthly
            ? this._monthlyReservation.phoneForm
            : this._transientReservation.phoneForm;
        const vehicleSelectForm = isMonthly
            ? await this._monthlyReservation.vehicleSelectForm()
            : await this._transientReservation.vehicleSelectForm();
        const contractNameForm = isMonthly
            ? this._monthlyReservation.contractNameForm
            : null;
        const vehicleColorSelectData =
            isMonthly && vehicleColorSelectForm
                ? vehicleColorSelectForm.submit()
                : true;
        const phoneData = phoneForm ? phoneForm.submit() : true;
        const vehicleSelectData =
            (isMonthly ||
                vehicleSelectPresent ||
                supportsOversizeFeeType ||
                licensePlateRequired) &&
            vehicleSelectForm
                ? vehicleSelectForm
                : true;
        const contractNameData =
            isMonthly && contractNameForm ? contractNameForm.submit() : true;

        if (phoneData && isObject(phoneData) && phoneData.smsParkingPass) {
            phoneData.smsParkingPass = true;
        }

        if (termsURL) {
            if (!this._monthlyReservation.termsAgreed) {
                this.setState({
                    termsError: true,
                });

                return false;
            } else {
                this.setState({
                    termsError: false,
                });
            }
        }

        if (
            (!phoneNumberRequired || phoneData) &&
            (!isMonthly || vehicleColorSelectData) &&
            (!isMonthly || contractNameData) &&
            ((!vehicleSelectRequired && !isMonthly) || vehicleSelectData)
        ) {
            let allData = {
                ...vehicleColorSelectData,
                ...contractNameData,
                ...phoneData,
            };

            if (isObject(vehicleSelectData)) {
                allData = {
                    ...allData,
                    ...vehicleSelectData,
                };
            }

            if (termsURL && this._monthlyReservation.termsAgreed) {
                allData.acceptedTerms = true;
            }

            return allData;
        } else {
            return false;
        }
    }

    render() {
        const {
            isMobile,
            isFirstView,
            user,
            isMonthly,
            selectedRate,
            hourlyRates,
            monthlyRates,
            reservationDetailsEdit,
            reservationDetailsSkipped,
            vehicleSelectRequired,
            licensePlateRequired,
            purchaseOnBehalfOfCustomer,
            purchaseForCustomer,
            isRebookingReservation,
            onVehicleChange,
            isLoadingRates,
        } = this.props;
        const {termsError} = this.state;
        const hasMultipleRates =
            (selectedRate &&
                (!isMonthly && hourlyRates && hourlyRates.length > 1)) ||
            (isMonthly && monthlyRates && monthlyRates.length > 1);
        const reservationPreview = this._renderPreview();
        const isOpen =
            (selectedRate &&
                user.status !== UserUtils.AUTH_STATE.LOGGED_OUT &&
                (reservationDetailsEdit ||
                    (!reservationPreview && !reservationDetailsSkipped) ||
                    (vehicleSelectRequired && isFirstView) ||
                    (licensePlateRequired && isFirstView) ||
                    (isFirstView && hasMultipleRates))) ||
            false;
        const isCompleted = reservationPreview || reservationDetailsSkipped;
        const classes = classNames('CheckoutPanel-reservation', {
            'CheckoutPanel-completed': isCompleted,
        });
        const continueBtnClasses = classNames('btn-continue-to-payment', {
            'admin-border-button':
                user.isAdmin &&
                !isMobile &&
                (purchaseForCustomer ||
                    purchaseOnBehalfOfCustomer ||
                    isRebookingReservation),
        });

        return (
            <CheckoutPanel
                className={classes}
                title={
                    <Fragment>
                        {isCompleted ? (
                            <em>
                                <IconCheck />
                            </em>
                        ) : (
                            <em>2</em>
                        )}{' '}
                        Reservation Details
                    </Fragment>
                }
                additionalTitleMarkup={
                    !isOpen && (reservationPreview || reservationDetailsSkipped)
                        ? this._renderReservationDetailEdit()
                        : null
                }
                open={isOpen}
                preview={reservationPreview}
            >
                {selectedRate && (
                    <div className="ReservationDetails">
                        {!isMonthly ? (
                            <TransientReservation
                                ref={node => {
                                    this._transientReservation = node;
                                }}
                                isOpen={isOpen}
                                onVehicleChange={onVehicleChange}
                                onIDontKnowChanged={this._onIDontKnowChanged}
                                isLoadingRates={isLoadingRates}
                            />
                        ) : (
                            <MonthlyReservation
                                ref={node => {
                                    this._monthlyReservation = node;
                                }}
                                isOpen={isOpen}
                                onVehicleChange={onVehicleChange}
                                termsError={termsError}
                                isLoadingRates={isLoadingRates}
                            />
                        )}
                        <div className="FormActions">
                            <Button
                                className={continueBtnClasses}
                                color="primary"
                                onClick={this._onContinueClick}
                                disabled={isLoadingRates}
                            >
                                Continue
                            </Button>
                        </div>
                    </div>
                )}
            </CheckoutPanel>
        );
    }
}

const mapStateToProps = state => {
    const {
        checkout: {
            reservationDetailsSkipped,
            reservationDetailsEdit,
            phoneNumberRequired,
            vehicleSelectRequired,
            vehicleSelectPresent,
            supportsOversizeFeeType,
            vehicleColor,
            isVehicleUnknown,
            licensePlateRequired,
            contractFullName,
            purchaseOnBehalfOfCustomer,
            purchaseForCustomer,
            isRebookingReservation,
        },
        searchRequest: {monthly: isMonthly, starts},
        spot: {
            selected: {
                selectedRate,
                hourly_rates: hourlyRates,
                monthly_rates: monthlyRates,
            },
        },
        user: {data: user},
    } = state;

    return {
        user,
        isMonthly,
        starts,
        selectedRate,
        hourlyRates,
        monthlyRates,
        reservationDetailsSkipped,
        reservationDetailsEdit,
        phoneNumberRequired,
        vehicleSelectRequired,
        vehicleSelectPresent,
        supportsOversizeFeeType,
        vehicleColor,
        isVehicleUnknown,
        licensePlateRequired,
        contractFullName,
        purchaseOnBehalfOfCustomer,
        purchaseForCustomer,
        isRebookingReservation,
    };
};

const mapDispatchToProps = {
    onUpdateData: updateData,
};

const enhance = compose(
    withConfig(['isMobile']),
    connect(
        mapStateToProps,
        mapDispatchToProps,
        null,
        {forwardRef: true}
    )
);

export default enhance(ReservationDetails);
